import { useLayoutEffect, useMemo, useRef } from "react";
import { Detailed } from "@react-three/drei";
import { IcosahedronGeometry, InstancedMesh, Object3D } from "three";
import { LOT, STREET, PUBLIC_WALK, SIDEWALK, ACCESSIBLE_BAY, PREOP, MOTO_TRACK, CAR_TRACK, BRAND_GREEN, FENCE_Z, SAFETY_STRIP } from "./layout";
import { useOptionalTexture, useChainLinkTexture } from "./materials";
import { InstancedCones } from "./Furniture";
import { SignDecal } from "./SignDecal";
import { MeetingPoint, PlasticBarriers, DeliveryZone, FireExtinguisher } from "./Safety";

/** Terreno, vías, aceras, pistas, cerramiento y vegetación de la sede. */
export function Exterior() {
  return (
    <group name="exterior">
      <Ground />
      <Street />
      <Sidewalk />
      <AccessibleBay />
      <PreopZone />
      <DeliveryZone />
      <MotoTrack />
      <CarTrack />
      <PerimeterFence />
      <StreetLights />
      <CrosswalkAndStop />
      <VisitorParking />
      <MeetingPoint />
      {/* extintor escénico de la zona de pistas */}
      <FireExtinguisher position={[CAR_TRACK.x - CAR_TRACK.w / 2 + 1.2, 0, CAR_TRACK.z - CAR_TRACK.d / 2 + 1.2]} />
      <Vegetation />
    </group>
  );
}

/** Terreno plano con franjas de césped en tono sobrio. */
function Ground() {
  const grassTex = useOptionalTexture("/assets/textures/cesped.png", [30, 24]);
  const concreteTex = useOptionalTexture("/assets/textures/Concrete034.jpg", [13, 9]);
  return (
    <group>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.02, 0]} receiveShadow>
        <planeGeometry args={[LOT.width, LOT.depth]} />
        <meshStandardMaterial color={grassTex ? "#b9c6ad" : "#6f8560"} roughness={1} map={grassTex ?? undefined} />
      </mesh>
      {/* plataforma de concreto alrededor del edificio */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.0, -8]} receiveShadow>
        <planeGeometry args={[52, 36]} />
        <meshStandardMaterial color={concreteTex ? "#b5b0a2" : "#bdb8a9"} roughness={0.95} map={concreteTex ?? undefined} />
      </mesh>
    </group>
  );
}

/** Bordillo corrido (acabado de andén). */
function Curb({ position, length, rotationY = 0 }: { position: [number, number, number]; length: number; rotationY?: number }) {
  return (
    <mesh position={position} rotation={[0, rotationY, 0]} castShadow>
      <boxGeometry args={[length, 0.14, 0.18]} />
      <meshStandardMaterial color="#a5a093" roughness={0.9} />
    </mesh>
  );
}

/** Línea central discontinua de la vía: un solo instancedMesh (18 tramos). */
function InstancedDashes() {
  const ref = useRef<InstancedMesh>(null);
  const count = 18;
  useLayoutEffect(() => {
    if (!ref.current) return;
    const dummy = new Object3D();
    for (let i = 0; i < count; i++) {
      dummy.position.set(-54 + i * 6.4, 0.02, STREET.z);
      dummy.rotation.set(-Math.PI / 2, 0, 0);
      dummy.updateMatrix();
      ref.current.setMatrixAt(i, dummy.matrix);
    }
    ref.current.instanceMatrix.needsUpdate = true;
  }, []);
  return (
    <instancedMesh ref={ref} args={[undefined, undefined, count]}>
      <planeGeometry args={[3, 0.15]} />
      <meshStandardMaterial color="#e8e6da" roughness={0.8} />
    </instancedMesh>
  );
}

/** Vía pública genérica FUERA del cerramiento, con andén público y demarcación. */
function Street() {
  const asphaltTex = useOptionalTexture("/assets/textures/Asphalt005.jpg", [40, 3]);
  return (
    <group name="via-publica">
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.01, STREET.z]} receiveShadow>
        <planeGeometry args={[LOT.width, STREET.width]} />
        <meshStandardMaterial color={asphaltTex ? "#5f6367" : "#4a4d4f"} roughness={0.95} map={asphaltTex ?? undefined} />
      </mesh>
      {/* línea central discontinua (instanciada) */}
      <InstancedDashes />
      {/* andén público entre el cerramiento (z=44) y la vía, con bordillo */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.04, PUBLIC_WALK.z]} receiveShadow>
        <planeGeometry args={[LOT.width, PUBLIC_WALK.width]} />
        <meshStandardMaterial color="#c9c4b6" roughness={0.9} />
      </mesh>
      <Curb position={[0, 0.07, PUBLIC_WALK.z + PUBLIC_WALK.width / 2]} length={LOT.width} />
    </group>
  );
}

/**
 * Andén peatonal segregado del recorrido vehicular
 * [NORMATIVO Anexo 60 2.1 Nota 1], de la fachada (z=-4) a la calle (z=44),
 * con paso deprimido de bordillo y barandas en el cruce público.
 */
function Sidewalk() {
  const length = SIDEWALK.z1 - SIDEWALK.z0;
  const cz = (SIDEWALK.z0 + SIDEWALK.z1) / 2;
  const rampZ = SIDEWALK.z1 - 1.2;
  return (
    <group name="circulacion-peatonal">
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[SIDEWALK.x, 0.03, cz]} receiveShadow>
        <planeGeometry args={[SIDEWALK.width, length]} />
        <meshStandardMaterial color="#cfcaba" roughness={0.9} />
      </mesh>
      {/* bordillos del corredor peatonal */}
      <Curb position={[SIDEWALK.x - SIDEWALK.width / 2 + 0.09, 0.07, cz]} length={length} rotationY={Math.PI / 2} />
      <Curb position={[SIDEWALK.x + SIDEWALK.width / 2 - 0.09, 0.07, cz]} length={length} rotationY={Math.PI / 2} />
      {/* paso deprimido de bordillo en el cruce del andén público [Ley 1618/2013]:
          pendiente real ≤ 8.33 % (desnivel 0.10 m en 1.2 m) con barandas */}
      <mesh rotation={[-Math.atan2(0.1, 1.2), 0, 0]} position={[SIDEWALK.x, 0.09, rampZ]}>
        <planeGeometry args={[SIDEWALK.width - 0.6, 1.25]} />
        <meshStandardMaterial color="#d8d2c0" roughness={0.85} />
      </mesh>
      {[-1, 1].map((s) => (
        <group key={s} position={[SIDEWALK.x + s * (SIDEWALK.width / 2 - 0.25), 0, rampZ]}>
          <mesh position={[0, 0.45, 0]} rotation={[Math.atan2(0.1, 1.2) - Math.PI / 2, 0, 0]} castShadow>
            <cylinderGeometry args={[0.025, 0.025, 1.3, 8]} />
            <meshStandardMaterial color="#7d8488" metalness={0.6} roughness={0.4} />
          </mesh>
          {[-0.5, 0.5].map((oz) => (
            <mesh key={oz} position={[0, 0.22, oz]}>
              <cylinderGeometry args={[0.02, 0.02, 0.44, 6]} />
              <meshStandardMaterial color="#7d8488" metalness={0.6} roughness={0.4} />
            </mesh>
          ))}
        </group>
      ))}
      <SignDecal text="PEATONAL" position={[SIDEWALK.x, 0.05, cz]} orientation="floor" variant="info" width={2.4} height={0.7} />
    </group>
  );
}

/** Bahía accesible señalizada [NORMATIVO Anexo 60 2.1 f), símbolo NTC 4139]. */
function AccessibleBay() {
  const { x, z, w, d } = ACCESSIBLE_BAY;
  // ruta accesible pintada bahía → acceso peatonal cubierto (-15, -2.5)
  const route = useMemo(() => {
    const x0 = x - w / 2;
    const z0 = z;
    const x1 = -15;
    const z1 = -2.5;
    const dx = x1 - x0;
    const dz = z1 - z0;
    const len = Math.hypot(dx, dz);
    return { cx: (x0 + x1) / 2, cz: (z0 + z1) / 2, len, rotY: Math.atan2(dx, dz) };
  }, [x, z, w]);
  return (
    <group name="bahia-accesible">
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[x, 0.025, z]} receiveShadow>
        <planeGeometry args={[w, d]} />
        <meshStandardMaterial color="#3f6ea5" roughness={0.85} />
      </mesh>
      {/* marco blanco */}
      {[
        [0, d / 2, w, 0.12],
        [0, -d / 2, w, 0.12],
        [w / 2, 0, 0.12, d],
        [-w / 2, 0, 0.12, d],
      ].map(([ox, oz, sw, sd], i) => (
        <mesh key={i} rotation={[-Math.PI / 2, 0, 0]} position={[x + ox, 0.035, z + oz]}>
          <planeGeometry args={[sw, sd]} />
          <meshStandardMaterial color="#f0efe8" roughness={0.8} />
        </mesh>
      ))}
      {/* franja de transferencia lateral de 1.2 m (diagonal blanca) */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[x + w / 2 + 0.6, 0.028, z]} receiveShadow>
        <planeGeometry args={[1.2, d]} />
        <meshStandardMaterial color="#d8d2c0" roughness={0.9} />
      </mesh>
      {[-1.5, -0.5, 0.5, 1.5].map((oz) => (
        <mesh key={oz} rotation={[-Math.PI / 2, 0, Math.PI / 4]} position={[x + w / 2 + 0.6, 0.036, z + oz]}>
          <planeGeometry args={[0.12, 1.4]} />
          <meshStandardMaterial color="#f0efe8" roughness={0.8} />
        </mesh>
      ))}
      {/* ruta accesible pintada bahía → acceso */}
      <mesh rotation={[-Math.PI / 2, 0, route.rotY]} position={[route.cx, 0.026, route.cz]}>
        <planeGeometry args={[0.35, route.len]} />
        <meshStandardMaterial color="#3f6ea5" roughness={0.85} />
      </mesh>
      <SignDecal text="♿" position={[x, 0.04, z]} orientation="floor" variant="info" width={1.6} height={1.6} />
      <SignDecal text="BAHÍA ACCESIBLE" position={[x, 1.9, z - d / 2 - 0.1]} variant="info" width={2.2} height={0.5} />
      {/* poste de señal */}
      <mesh position={[x, 0.95, z - d / 2 - 0.15]} castShadow>
        <cylinderGeometry args={[0.04, 0.04, 1.9, 8]} />
        <meshStandardMaterial color="#7d8488" metalness={0.6} roughness={0.4} />
      </mesh>
    </group>
  );
}

/** Franjas de seguridad de 1 m en los CUATRO bordes de la pista [NORMATIVO Anexo 60 2.3.1 Nota 6]. */
function SafetyStrips({ x, z, w, d }: { x: number; z: number; w: number; d: number }) {
  const s = SAFETY_STRIP;
  return (
    <group>
      {[-w / 2 + s / 2, w / 2 - s / 2].map((ox, i) => (
        <mesh key={i} rotation={[-Math.PI / 2, 0, 0]} position={[x + ox, 0.03, z]}>
          <planeGeometry args={[s, d - 0.4]} />
          <meshStandardMaterial color="#d8b13c" roughness={0.85} />
        </mesh>
      ))}
      {[-d / 2 + s / 2, d / 2 - s / 2].map((oz, i) => (
        <mesh key={i} rotation={[-Math.PI / 2, 0, 0]} position={[x, 0.03, z + oz]}>
          <planeGeometry args={[w - 0.4, s]} />
          <meshStandardMaterial color="#d8b13c" roughness={0.85} />
        </mesh>
      ))}
    </group>
  );
}

/**
 * Zona preoperacional exterior techada [NORMATIVO Anexo 60 2.2.7]:
 * cubierta metálica sobre 2 bahías de alistamiento, junto al inicio de pista.
 */
function PreopZone() {
  const { x, z, w, d, roofHeight } = PREOP;
  return (
    <group name="zona-preoperacional">
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[x, 0.02, z]} receiveShadow>
        <planeGeometry args={[w, d]} />
        <meshStandardMaterial color="#a8a498" roughness={0.9} />
      </mesh>
      {/* cubierta */}
      <mesh position={[x, roofHeight, z]} castShadow>
        <boxGeometry args={[w + 1, 0.15, d + 1]} />
        <meshStandardMaterial color="#7d8a8c" roughness={0.6} metalness={0.3} />
      </mesh>
      {[
        [x - w / 2 + 0.4, z - d / 2 + 0.4],
        [x + w / 2 - 0.4, z - d / 2 + 0.4],
        [x - w / 2 + 0.4, z + d / 2 - 0.4],
        [x + w / 2 - 0.4, z + d / 2 - 0.4],
      ].map(([px, pz], i) => (
        <mesh key={i} position={[px, roofHeight / 2, pz]} castShadow>
          <cylinderGeometry args={[0.09, 0.09, roofHeight, 10]} />
          <meshStandardMaterial color="#5a6064" metalness={0.6} roughness={0.4} />
        </mesh>
      ))}
      {/* demarcación de bahías de alistamiento */}
      {[-w / 4, w / 4].map((ox) => (
        <mesh key={ox} rotation={[-Math.PI / 2, 0, 0]} position={[x + ox, 0.03, z]}>
          <planeGeometry args={[0.12, d - 1]} />
          <meshStandardMaterial color="#e8e6da" roughness={0.8} />
        </mesh>
      ))}
      <SignDecal text="REVISIÓN PREOPERACIONAL" position={[x, roofHeight - 0.5, z + d / 2 + 0.6]} variant="plate" width={3.4} height={0.5} />
    </group>
  );
}

/**
 * Pista de motocicleta (30 × 15 m): pavimento, circuito señalizado
 * con conos y zonas de seguridad de 1 m [NORMATIVO Anexo 60 2.3.1].
 */
function MotoTrack() {
  const { x, z, w, d } = MOTO_TRACK;
  const asphaltTex = useOptionalTexture("/assets/textures/Asphalt005.jpg", [10, 5]);
  const cones = useMemo(() => {
    const pts: [number, number][] = [];
    // doble fila de conos marcando un zigzag de destreza
    for (let i = 0; i < 8; i++) pts.push([x - w / 2 + 4 + i * 3.2, z - 2.5]);
    for (let i = 0; i < 8; i++) pts.push([x - w / 2 + 4 + i * 3.2, z + 2.5]);
    return pts;
  }, [x, z, w]);
  return (
    <group name="pista-moto">
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[x, 0.02, z]} receiveShadow>
        <planeGeometry args={[w, d]} />
        <meshStandardMaterial color={asphaltTex ? "#5f6367" : "#54575a"} roughness={0.95} map={asphaltTex ?? undefined} />
      </mesh>
      {/* franjas de seguridad de 1 m en los cuatro bordes + barreras plásticas enlazadas */}
      <SafetyStrips x={x} z={z} w={w} d={d} />
      <PlasticBarriers x={x} z={z} w={w} d={d} />
      {/* línea de salida */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[x - w / 2 + 2, 0.03, z]}>
        <planeGeometry args={[0.25, 6]} />
        <meshStandardMaterial color="#f0efe8" roughness={0.8} />
      </mesh>
      {/* cajón de inicio: el aspirante lleva la moto hasta aquí con el motor
          apagado y la apoya en su soporte [NORMATIVO Anexo 60 num. 2.5.5.1] */}
      <group position={[x - w / 2 + 4, 0, z]}>
        {[
          [0, 0.6, 1.4, 0.1],
          [0, -0.6, 1.4, 0.1],
          [0.65, 0, 0.1, 1.3],
          [-0.65, 0, 0.1, 1.3],
        ].map(([ox, oz, sw, sd], i) => (
          <mesh key={i} rotation={[-Math.PI / 2, 0, 0]} position={[ox, 0.035, oz]}>
            <planeGeometry args={[sw, sd]} />
            <meshStandardMaterial color="#f0efe8" roughness={0.8} />
          </mesh>
        ))}
        <SignDecal text="CAJÓN DE INICIO" position={[0, 0.04, 1.6]} orientation="floor" variant="plate" width={2.2} height={0.4} />
      </group>
      <InstancedCones points={cones} />
      <SignDecal text="PISTA MOTOCICLETA" position={[x, 2.2, z - d / 2 - 0.2]} variant="plate" width={3} height={0.5} />
    </group>
  );
}

/**
 * Pista de automóvil (60 × 25 m): trazado demarcado con líneas y conos,
 * separada físicamente de la pista de motos [NORMATIVO Anexo 60 2.1 Nota 1].
 */
function CarTrack() {
  const { x, z, w, d } = CAR_TRACK;
  const asphaltTex = useOptionalTexture("/assets/textures/Asphalt005.jpg", [20, 8]);
  const cones = useMemo(() => {
    const pts: [number, number][] = [];
    // zigzag central y bahía de parqueo en reversa
    for (let i = 0; i < 10; i++) pts.push([x - w / 2 + 8 + i * 4.4, z - 4]);
    for (let i = 0; i < 10; i++) pts.push([x - w / 2 + 8 + i * 4.4, z + 4]);
    for (let i = 0; i < 4; i++) pts.push([x + w / 2 - 12 + i * 2.4, z - d / 2 + 4]);
    return pts;
  }, [x, z, w, d]);
  return (
    <group name="pista-auto">
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[x, 0.02, z]} receiveShadow>
        <planeGeometry args={[w, d]} />
        <meshStandardMaterial color={asphaltTex ? "#5f6367" : "#54575a"} roughness={0.95} map={asphaltTex ?? undefined} />
      </mesh>
      {/* carriles demarcados */}
      {[-d / 4, 0, d / 4].map((oz) => (
        <mesh key={oz} rotation={[-Math.PI / 2, 0, 0]} position={[x, 0.03, z + oz]}>
          <planeGeometry args={[w - 4, 0.15]} />
          <meshStandardMaterial color="#e8e6da" roughness={0.8} />
        </mesh>
      ))}
      {/* bahía de parqueo demarcada */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[x + w / 2 - 9, 0.03, z - d / 2 + 3.4]}>
        <planeGeometry args={[5, 0.15]} />
        <meshStandardMaterial color="#f0efe8" roughness={0.8} />
      </mesh>
      {/* línea de salida */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[x - w / 2 + 3, 0.03, z]}>
        <planeGeometry args={[0.25, 8]} />
        <meshStandardMaterial color="#f0efe8" roughness={0.8} />
      </mesh>
      {/* franjas de seguridad de 1 m en los cuatro bordes + barreras plásticas enlazadas */}
      <SafetyStrips x={x} z={z} w={w} d={d} />
      <PlasticBarriers x={x} z={z} w={w} d={d} />
      <InstancedCones points={cones} />
      <SignDecal text="PISTA AUTOMÓVIL" position={[x, 2.2, z - d / 2 - 0.2]} variant="plate" width={3} height={0.5} />
    </group>
  );
}

/** Poste de alumbrado público: columna cónica, brazo y cabezal. */
function StreetLight({ position, rotationY = 0 }: { position: [number, number, number]; rotationY?: number }) {
  return (
    <group position={position} rotation={[0, rotationY, 0]}>
      <mesh position={[0, 3, 0]} castShadow>
        <cylinderGeometry args={[0.06, 0.1, 6, 10]} />
        <meshStandardMaterial color="#4a4f52" metalness={0.6} roughness={0.4} />
      </mesh>
      <mesh position={[0, 0.15, 0]}>
        <cylinderGeometry args={[0.18, 0.22, 0.3, 10]} />
        <meshStandardMaterial color="#5a6064" roughness={0.8} />
      </mesh>
      <mesh position={[0.8, 5.85, 0]} rotation={[0, 0, -0.18]} castShadow>
        <cylinderGeometry args={[0.04, 0.05, 1.8, 8]} />
        <meshStandardMaterial color="#4a4f52" metalness={0.6} roughness={0.4} />
      </mesh>
      <mesh position={[1.6, 5.7, 0]}>
        <boxGeometry args={[0.62, 0.14, 0.24]} />
        <meshStandardMaterial color="#3c4145" roughness={0.5} />
      </mesh>
      <mesh position={[1.6, 5.62, 0]}>
        <boxGeometry args={[0.5, 0.03, 0.18]} />
        <meshStandardMaterial color="#fff4d8" emissive="#ffedb8" emissiveIntensity={0.6} />
      </mesh>
    </group>
  );
}

/** Alumbrado del andén público y del acceso vehicular. */
function StreetLights() {
  return (
    <group name="alumbrado">
      <StreetLight position={[-40, 0, 44.8]} rotationY={Math.PI} />
      <StreetLight position={[-6, 0, 44.8]} rotationY={Math.PI} />
      <StreetLight position={[28, 0, 44.8]} rotationY={Math.PI} />
      <StreetLight position={[-24, 0, 8]} />
      <StreetLight position={[36, 0, -4]} rotationY={Math.PI / 2} />
    </group>
  );
}

/** Paso de cebra sobre la vía pública frente al acceso peatonal + señal PARE del portón. */
function CrosswalkAndStop() {
  return (
    <group name="cruce-y-señal">
      {Array.from({ length: 6 }, (_, i) => (
        <mesh key={i} rotation={[-Math.PI / 2, 0, 0]} position={[-21.5 + i * 1.2, 0.02, STREET.z]}>
          <planeGeometry args={[0.55, STREET.width - 1]} />
          <meshStandardMaterial color="#e8e6da" roughness={0.85} />
        </mesh>
      ))}
      {/* PARE octogonal en el portón vehicular */}
      <group position={[52.5, 0, 43]}>
        <mesh position={[0, 1.3, 0]} castShadow>
          <cylinderGeometry args={[0.04, 0.04, 2.6, 8]} />
          <meshStandardMaterial color="#7d8488" metalness={0.6} roughness={0.4} />
        </mesh>
        <mesh position={[0, 2.5, 0]} rotation={[0, Math.PI, 0]}>
          <circleGeometry args={[0.42, 8]} />
          <meshStandardMaterial color="#b03a2e" roughness={0.6} side={2} />
        </mesh>
        <SignDecal text="PARE" position={[0, 2.5, -0.02]} rotationY={Math.PI} variant="info" width={0.66} height={0.3} />
      </group>
    </group>
  );
}

/** Parqueadero de visitantes: 6 bahías demarcadas junto al portón, dos ocupadas. */
function VisitorParking() {
  const bays = Array.from({ length: 6 }, (_, i) => 30 + i * 3);
  return (
    <group name="parqueadero-visitantes">
      {bays.map((bx) => (
        <mesh key={bx} rotation={[-Math.PI / 2, 0, 0]} position={[bx + 1.25, 0.035, 38]}>
          <planeGeometry args={[0.12, 4.6]} />
          <meshStandardMaterial color="#e8e6da" roughness={0.8} />
        </mesh>
      ))}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[37.5, 0.035, 40.3]}>
        <planeGeometry args={[15, 0.12]} />
        <meshStandardMaterial color="#e8e6da" roughness={0.8} />
      </mesh>
      {/* dos vehículos genéricos estacionados (sin rótulos, sin placas) */}
      <ParkedCar position={[32.5, 0, 38]} color="#7a8894" />
      <ParkedCar position={[38.5, 0, 38]} color="#8c7f70" />
    </group>
  );
}

/** Vehículo genérico estacionado (procedural, escala real, sin marca). */
function ParkedCar({ position, color }: { position: [number, number, number]; color: string }) {
  return (
    <group position={position} rotation={[0, Math.PI / 2, 0]}>
      <mesh position={[0, 0.55, 0]} castShadow receiveShadow>
        <boxGeometry args={[1.75, 0.5, 4.2]} />
        <meshStandardMaterial color={color} roughness={0.3} metalness={0.25} />
      </mesh>
      <mesh position={[0, 1.02, -0.1]} castShadow>
        <boxGeometry args={[1.55, 0.45, 2.2]} />
        <meshStandardMaterial color="#2c3438" roughness={0.12} metalness={0.35} />
      </mesh>
      {[[-0.8, 1.3], [0.8, 1.3], [-0.8, -1.3], [0.8, -1.3]].map(([x, z], i) => (
        <mesh key={i} position={[x, 0.32, z]} rotation={[0, 0, Math.PI / 2]} castShadow>
          <cylinderGeometry args={[0.32, 0.32, 0.2, 14]} />
          <meshStandardMaterial color="#1c1f21" roughness={0.9} />
        </mesh>
      ))}
    </group>
  );
}

/** Postes instanciados del cerramiento: una sola llamada de dibujo por panel. */
function InstancedPosts({ length, height }: { length: number; height: number }) {
  const ref = useRef<InstancedMesh>(null);
  const count = Math.floor(length / 5) + 1;
  const matrices = useMemo(() => {
    const dummy = new Object3D();
    return Array.from({ length: count }, (_, j) => {
      dummy.position.set(-length / 2 + j * 5, 0, 0);
      dummy.updateMatrix();
      return dummy.matrix.clone();
    });
  }, [count, length]);
  useLayoutEffect(() => {
    if (!ref.current) return;
    matrices.forEach((m, i) => ref.current!.setMatrixAt(i, m));
    ref.current.instanceMatrix.needsUpdate = true;
  }, [matrices]);
  return (
    <instancedMesh ref={ref} args={[undefined, undefined, count]} castShadow>
      <cylinderGeometry args={[0.05, 0.05, height + 0.1, 8]} />
      <meshStandardMaterial color="#5a6064" metalness={0.6} roughness={0.4} />
    </instancedMesh>
  );
}

/**
 * Cerramiento perimetral con malla (textura alpha procedural). El frente
 * sur va en z = 44, DENTRO del lote: la vía pública queda fuera.
 * Barrera de separación entre pista de motos y de autos extendida
 * hasta el borde sur de la pista de autos (z = 34.5).
 */
function PerimeterFence() {
  const chainLink = useChainLinkTexture();
  const H = 2;
  const panels = useMemo(() => {
    const hw = LOT.width / 2;
    const hd = LOT.depth / 2;
    const fz = FENCE_Z;
    const list: { position: [number, number, number]; length: number; rotY: number }[] = [
      { position: [0, H / 2, -hd], length: LOT.width, rotY: 0 },
      { position: [-hw, H / 2, 0], length: LOT.depth, rotY: Math.PI / 2 },
      { position: [hw, H / 2, 0], length: LOT.depth, rotY: Math.PI / 2 },
    ];
    // frente sur (z=44) con portón vehicular (x 38..50) y peatonal (x -22..-18)
    list.push({ position: [(-hw - 22) / 2, H / 2, fz], length: hw - 22, rotY: 0 });
    list.push({ position: [(-18 + 38) / 2, H / 2, fz], length: 56, rotY: 0 });
    list.push({ position: [(50 + hw) / 2, H / 2, fz], length: hw - 50, rotY: 0 });
    // barrera entre pista moto y pista auto: de z=-30 a z=34.5
    list.push({ position: [-22, H / 2, (-30 + 34.5) / 2], length: 64.5, rotY: Math.PI / 2 });
    return list;
  }, []);

  return (
    <group name="cerramiento">
      {panels.map((p, i) => (
        <group key={i} position={p.position} rotation={[0, p.rotY, 0]}>
          <mesh>
            <planeGeometry args={[p.length, H]} />
            <meshStandardMaterial map={chainLink} transparent alphaTest={0.3} side={2} roughness={0.5} metalness={0.6} />
          </mesh>
          <InstancedPosts length={p.length} height={H} />
        </group>
      ))}
    </group>
  );
}

/**
 * Árbol andino low-poly procedural: tronco con ligera inclinación y
 * copa icosaédrica con ruido en vértices. Tres variantes de porte.
 */
function AndeanTree({ variant = 0 }: { variant?: 0 | 1 | 2 }) {
  // copa en 3 masas de follaje (más orgánico que un solo icosaedro)
  const blobs = useMemo(() => {
    const radius = [1.7, 1.25, 1.45][variant];
    const mk = (r: number, seed: number, sy: number) => {
      const geo = new IcosahedronGeometry(r, 2);
      const pos = geo.attributes.position;
      for (let i = 0; i < pos.count; i++) {
        const n = 1 + 0.14 * Math.sin(i * 3.7 + seed) + 0.07 * Math.cos(i * 1.9 + seed);
        pos.setX(i, pos.getX(i) * n);
        pos.setZ(i, pos.getZ(i) * n);
        pos.setY(i, pos.getY(i) * sy * (1 + 0.08 * Math.sin(i * 2.3 + seed)));
      }
      geo.computeVertexNormals();
      return geo;
    };
    return [
      { geo: mk(radius, variant, 1.15), pos: [0, 0.35, 0] as const, tone: ["#3f6134", "#527539", "#486b3c"][variant] },
      { geo: mk(radius * 0.68, variant + 5, 1.0), pos: [radius * 0.62, -0.15, radius * 0.3] as const, tone: ["#4c7039", "#5d8148", "#557a44"][variant] },
      { geo: mk(radius * 0.58, variant + 9, 1.05), pos: [-radius * 0.58, 0.05, -radius * 0.34] as const, tone: ["#37592e", "#496b32", "#406236"][variant] },
    ];
  }, [variant]);
  const trunkH = [2.8, 2.0, 2.4][variant];
  const lean = [0.05, -0.08, 0.11][variant];
  return (
    <group rotation={[0, 0, lean]}>
      <mesh position={[0, trunkH / 2, 0]} castShadow>
        <cylinderGeometry args={[0.13, 0.24, trunkH, 8]} />
        <meshStandardMaterial color="#54422f" roughness={0.95} />
      </mesh>
      {[0.5, -0.4].map((dir, i) => (
        <mesh key={i} position={[dir * 0.5, trunkH * 0.85, 0]} rotation={[0, 0, dir]}>
          <cylinderGeometry args={[0.05, 0.09, 1.2, 6]} />
          <meshStandardMaterial color="#54422f" roughness={0.95} />
        </mesh>
      ))}
      <group position={[lean * -trunkH, trunkH + [1.55, 1.0, 1.3][variant], 0]}>
        {blobs.map((b, i) => (
          <mesh key={i} geometry={b.geo} position={[b.pos[0], b.pos[1], b.pos[2]]} castShadow>
            <meshStandardMaterial color={b.tone} roughness={0.92} />
          </mesh>
        ))}
      </group>
    </group>
  );
}

/** Arbusto compacto (icosaedro deformado, dos tonos). */
function Shrub({ position, scale = 1, tone = "#4d7040" }: { position: [number, number, number]; scale?: number; tone?: string }) {
  return (
    <group position={position} scale={scale}>
      <mesh position={[0, 0.32, 0]} castShadow>
        <icosahedronGeometry args={[0.42, 1]} />
        <meshStandardMaterial color={tone} roughness={0.95} flatShading />
      </mesh>
      <mesh position={[0.14, 0.5, 0.1]} castShadow>
        <icosahedronGeometry args={[0.28, 1]} />
        <meshStandardMaterial color="#5d8148" roughness={0.95} flatShading />
      </mesh>
    </group>
  );
}

/** Jardineras de concreto con arbustos: flanquean la marquesina y el zócalo. */
function ShrubsAndPlanters() {
  const facadeSpots: [number, number][] = [
    [-18.5, -3.2], [-12, -3.2], [-6, -3.2], [0, -3.2], [6, -3.2], [12, -3.2], [18.5, -3.2],
  ];
  return (
    <group name="jardineras">
      {/* jardineras de la marquesina */}
      {[-19.2, -10.8].map((px) => (
        <group key={px} position={[px, 0, -2.6]}>
          <mesh position={[0, 0.25, 0]} castShadow receiveShadow>
            <boxGeometry args={[1.1, 0.5, 1.1]} />
            <meshStandardMaterial color="#b5b0a2" roughness={0.9} />
          </mesh>
          <Shrub position={[0, 0.4, 0]} scale={1.15} />
        </group>
      ))}
      {/* hilera de arbustos junto al zócalo frontal */}
      {facadeSpots.map(([px, pz], i) => (
        <Shrub key={i} position={[px, 0, pz]} scale={0.9 + (i % 3) * 0.15} tone={i % 2 ? "#446638" : "#4d7040"} />
      ))}
      {/* arbustos junto al andén peatonal */}
      {[-16.8, -23.2].flatMap((px) =>
        [2, 10, 18, 26, 34].map((pz, i) => (
          <Shrub key={`${px}-${pz}`} position={[px, 0, pz]} scale={0.75 + (i % 2) * 0.2} tone={i % 2 ? "#567a42" : "#446638"} />
        )),
      )}
    </group>
  );
}

/**
 * 10 árboles con LOD por distancia (drei Detailed): copa detallada cerca,
 * copa de baja densidad lejos. Tres variantes.
 */
function Vegetation() {
  const spots: { p: [number, number]; v: 0 | 1 | 2 }[] = [
    { p: [-54, 30], v: 0 },
    { p: [-48, -38], v: 1 },
    { p: [-30, -40], v: 2 },
    { p: [26, -38], v: 1 },
    { p: [48, -34], v: 0 },
    { p: [54, 8], v: 2 },
    { p: [-54, 8], v: 0 },
    { p: [52, 36], v: 1 },
    { p: [-8, 38], v: 2 },
    { p: [56, 38], v: 1 },
  ];
  return (
    <group name="vegetacion">
      {spots.map(({ p, v }, i) => (
        <group key={i} position={[p[0], 0, p[1]]}>
          <Detailed distances={[0, 45]}>
            <AndeanTree variant={v} />
            {/* LOD lejano: misma silueta, sin deformación y menos segmentos */}
            <group>
              <mesh position={[0, [2.8, 2.0, 2.4][v] / 2, 0]}>
                <cylinderGeometry args={[0.15, 0.24, [2.8, 2.0, 2.4][v], 5]} />
                <meshStandardMaterial color="#5d4a36" roughness={1} />
              </mesh>
              <mesh position={[0, [4.4, 3, 3.7][v], 0]}>
                <icosahedronGeometry args={[[1.7, 1.25, 1.45][v], 0]} />
                <meshStandardMaterial color={["#446638", "#567a42", "#4d7040"][v]} roughness={1} flatShading />
              </mesh>
            </group>
          </Detailed>
        </group>
      ))}
      {/* franja verde institucional junto al acceso */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[-15, 0.015, 8]} receiveShadow>
        <planeGeometry args={[14, 2]} />
        <meshStandardMaterial color={BRAND_GREEN} roughness={1} />
      </mesh>
      <ShrubsAndPlanters />
    </group>
  );
}
